import React, { useState, FC, useEffect } from 'react';
import { Row, Col, Spinner, Badge } from 'react-bootstrap';
import { SimulationResultSummary } from '../core/_models';
import { formatNumber, getNameByISO3, shortenLargeNumber } from '../../../../libs/misc';
import { useAuth } from '../../../../modules/auth';


type SummaryCardProps = {
  summary?: SimulationResultSummary
  rep?: string
  par?: string
  year?: number
}

const SummaryCard:FC<SummaryCardProps> = ({summary, rep, par, year})=>{
  const [isLoading, setIsLoading] = useState(true)
  const [reporter, setReporter] = useState<string | null>()
  const [partner, setPartner] = useState<string | null>()
  const [selectedYear, setSelectedYear] = useState<number | null>()
  const [summaryResult, setSummaryResult] = useState<SimulationResultSummary | null>()
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;
  

  useEffect(()=>{
    setSummaryResult(summary)
    setReporter(rep)
    setPartner(par)
    setSelectedYear(year)
    setIsLoading(false)

  },[summary, rep, par, year])

  // Cleanup function to reset the state when the component is unmounted 
  useEffect(()=>{
    setSummaryResult(null); 
    setReporter(null); 
    setPartner(null); 
    setSelectedYear(null); 
    setIsLoading(true);
    
    return () => { 
      setSummaryResult(null); 
      setReporter(null); 
      setPartner(null); 
      setSelectedYear(null); 
      setIsLoading(true);
    };
  },[])
  
  const percentCurrentTotal = (+summaryResult?.result_sum_total!/+summaryResult?.result_total_imports!)*100
  const percentCurrentTradeValue = (+summaryResult?.result_sum_total!/+summaryResult?.result_sum_trade_val!)*100
  
  return (
    <>
      {!isLoading && summaryResult? (
        <>
          <Row className='my-3'>
            <Col md={6} className='px-5'>
              <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
                <div className='symbol symbol-45px me-5'>
                  <i className="fa-solid fa-percent"></i>
                </div>

                <div className='d-flex flex-column'>
                  <div className='text-gray-800 text-hover-primary fs-6 fw-bold'>
                    {percentCurrentTradeValue > 0 
                      ? ( <>An <span className="text-success me-1">increase of {percentCurrentTradeValue.toFixed(2)}%</span></>) 
                      : percentCurrentTradeValue === 0 
                        ? `No change`
                        :( <>A <span className="text-danger me-1">A decrease of {percentCurrentTradeValue.toFixed(2)}%</span></> )
                    }(by trade value) of imports of affected commodities by {partner} from {reporter}
                  </div>

                  <span className='text-gray-500 fw-semibold'>
                    Representing 
                    {percentCurrentTotal > 0 
                      ? ( <> an <span className="text-success me-1">increase of {percentCurrentTotal.toFixed(0)}%</span></>) 
                      : percentCurrentTotal === 0 
                        ? ` no change `
                        :( <>a <span className="text-danger me-1">decrease of {percentCurrentTotal.toFixed(0)}%</span></> )
                    }
                    in total imports of {partner} from {reporter} in {selectedYear}
                  </span>
                </div>
              </div>
            </Col>
            {reporter?.toLowerCase()===getNameByISO3(reporterISO3User).toLowerCase() 
            || partner?.toLowerCase()===getNameByISO3(reporterISO3User).toLowerCase() ?(
              <Col md={6}>
                <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
                  <div className='symbol symbol-45px me-5'>
                    <i className="fa-solid fa-list-check"></i>
                  </div>

                  <div className='d-flex flex-column'>
                    <div className='text-gray-800 text-hover-primary fs-6 fw-bold'>
                      Trade creation for {reporter}: 
                      <Badge className="ms-2 text-white fs-7" 
                          bg={(summaryResult?.result_sum_total!)>0?'success':'danger'}>
                          US$ {shortenLargeNumber(summaryResult?.result_sum_total!)}
                      </Badge>
                    </div>

                    <span className='text-gray-500 fw-semibold'>
                      Consisting of a trade creation US$ {shortenLargeNumber(summaryResult?.result_sum_trade_creation)} and trade diversion of US$ {shortenLargeNumber(summaryResult?.result_sum_trade_diversion)}
                    </span>
                  </div>
                </div>
              </Col>
            ):(
              <Col md={6} className='px-5'>
                <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
                  <div className='symbol symbol-45px me-5'>
                    <i className="fa-regular fa-clipboard"></i>
                  </div>

                  <div className='d-flex flex-column'>
                    <div className='text-gray-800 text-hover-primary fs-6 fw-bold'>
                      Total impact for {getNameByISO3(reporterISO3User)}: 
                      <Badge className="ms-2 text-white fs-7" bg="danger">
                        US$ {shortenLargeNumber(summaryResult?.result_sum_tctd_selected_econ_impacted)}
                      </Badge>
                    </div>

                    <span className='text-gray-500 fw-semibold'>Analysis shows a significant negative financial impact of US$ {shortenLargeNumber(summaryResult?.result_sum_tctd_selected_econ_impacted)} for {getNameByISO3(reporterISO3User)}, highlighting economic challenges.</span>
                  </div>
                </div>
              </Col>
            )}
            
          </Row>
        </>
        ):(
          <div className="d-flex align-items-center justify-content-center" style={{ height: '10vh' }}>
            <Spinner animation="border" role="status" variant="primary" className="me-3" />
            <div className="loading-text">
              Loading
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </div>
          </div>
        )}
    </>
  )

}

export {SummaryCard}